/**
 * RestorationIronRepairs — Stage-1 builder for Long-Run iron roofs.
 *
 * Sections (Feb '26 operator spec):
 *   • Rescrew         — job-wide $/m² with replacement tek screws.
 *   • Ridges          — per-pin from the roof diagram, $/lm.
 *   • Sheets          — operator-added rows (qty × length × $/lm).
 *   • Barges          — per-pin from the diagram, $/lm.
 *   • Valleys         — per-pin from the diagram, $/lm.
 *   • Pipe/Flue Flashings — per-pin Btray + Dektite combo.
 *
 * Single-quote mode (no scenarios) per the operator's preference for
 * the first cut. Stage-2 Paint reuses the existing Stage2PaintSection
 * component — the backend `_subtype_paint_match` was extended to map
 * Long-Run → "Long-Run 3-stage" paint system seeded in iter 103.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Save, AlertCircle, AlertTriangle, Wrench, Pipette, GripHorizontal, Triangle, Plus, Trash2, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Stage2PaintSection } from '@/components/RestorationConcreteRepairs';
import EditRateLink from '@/components/EditRateLink';
import RescrewDetailModal from '@/components/RescrewDetailModal';
import IronRateDetailModal from '@/components/IronRateDetailModal';
import ComponentsFlashingEmbed from '@/components/shared/ComponentRow/ComponentsFlashingEmbed';

const IRON_SUBTYPES = new Set([
  'long-run', 'longrun', 'long run', 'lr', 'iron',
]);

export function isIronSubtype(subtype) {
  return IRON_SUBTYPES.has(((subtype || '').toString().toLowerCase().trim()));
}

export default function RestorationIronRepairs({ api, appraisalId, subtype, onLinesChanged, onJumpToPin, onOpenAnnotator, paintHiddenByDefault = false }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState({ lines: [], total: 0, line_count: 0, stage_totals: { stage_1: 0, stage_2: 0 }, materials_total: 0, labour_total: 0, split_delta: 0 });
  // v10.29 Sprint 4b · Slice C (2026-02c) — Fast-jump navigator.
  const jumpToComponents = useCallback((componentType) => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', 'Components');
    params.set('focus_section', componentType || 'Ridge');
    navigate(`?${params.toString()}`, { replace: false });
  }, [navigate]);
  // v10.29 Sprint 4b · Slice H (2026-02c) — CalcShadow toggle.
  const [showCalcs, setShowCalcs] = useState(() => {
    try { return typeof sessionStorage !== 'undefined' && sessionStorage.getItem('repairs_show_calcs') === '1'; }
    catch { return false; }
  });
  const toggleCalcs = useCallback(() => {
    setShowCalcs((v) => {
      const next = !v;
      try { sessionStorage.setItem('repairs_show_calcs', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);
  // v10.29 Sprint 4b · Yellow banner — per-appraisal dismiss.
  const bannerKey = `repairs_banner_dismissed_${appraisalId}`;
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return typeof localStorage !== 'undefined' && localStorage.getItem(bannerKey) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { setBannerDismissed(typeof localStorage !== 'undefined' && localStorage.getItem(bannerKey) === '1'); }
    catch { /* ignore */ }
  }, [bannerKey]);
  const dismissBanner = useCallback(() => {
    try { localStorage.setItem(bannerKey, '1'); } catch { /* ignore */ }
    setBannerDismissed(true);
  }, [bannerKey]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Iter211 — when the parent flags this as a Repairs-context render
  // (i.e. NOT a Restoration Repairs+Paint workflow), Stage 2 paint is
  // hidden by default and the operator must explicitly opt-in via a
  // "Select painting info" button. Stays expanded once opened.
  const [paintRevealed, setPaintRevealed] = useState(!paintHiddenByDefault);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/restoration/appraisals/${appraisalId}/iron-repairs/config`);
      const { data: p } = await api.get(`/restoration/appraisals/${appraisalId}/iron-repairs/preview`);
      setData(d);
      setPreview(p);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }, [api, appraisalId]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (cfg) => {
    setBusy(true);
    setErr(null);
    try {
      await api.put(`/restoration/appraisals/${appraisalId}/iron-repairs/config`, cfg);
      const { data: p } = await api.get(`/restoration/appraisals/${appraisalId}/iron-repairs/preview`);
      setPreview(p);
      if (onLinesChanged) await onLinesChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [api, appraisalId, onLinesChanged]);

  const update = (sectionPatch) => {
    const next = { ...data.config, ...sectionPatch };
    setData(d => ({ ...d, config: next }));
    save(next);
  };

  // Iter216 — Confirm/Unconfirm pins. Calls the backend bulk endpoint
  // then refetches config so pending_counts + the per-pin `confirmed`
  // flags re-render with the authoritative state. Toast confirms the
  // action and surfaces backend warnings (e.g. "pin R3 missing length").
  //
  // Iter223 follow-up — apply an OPTIMISTIC patch to the local config
  // before the network round-trip so the amber confirm-required banner
  // clears instantly on click. The subsequent `load()` reconciles in
  // case the server rejected/overrode anything.
  const confirmPins = useCallback(async (section, rawIds, confirmed) => {
    // iter303ak-fix23i · Trevor: `setCfg is not defined` on pin confirm
    // (2nd occurrence — same paste bug as Tin Tile fix23h, but this
    // file uses [data, setData] with sections nested under data.config,
    // and it's multi-section (ridge/barge/valley/flashing/sheet).
    setData((d) => {
      if (!d || !d.config) return d;
      const targetSections = section === 'all'
        ? ['ridge', 'barge', 'valley', 'flashing', 'sheet']
        : [section];
      const nextConfig = { ...d.config };
      for (const s of targetSections) {
        const node = nextConfig[s];
        if (!node || !Array.isArray(node.items)) continue;
        nextConfig[s] = {
          ...node,
          items: node.items.map((it) =>
            rawIds && !rawIds.includes(it.raw_id)
              ? it
              : { ...it, confirmed: !!confirmed }
          ),
        };
      }
      return { ...d, config: nextConfig };
    });
    try {
      const { data: r } = await api.post(
        `/restoration/appraisals/${appraisalId}/iron-repairs/confirm`,
        { section, raw_ids: rawIds, confirmed },
      );
      if (r?.warnings?.length) {
        toast.warning(`${confirmed ? 'Confirmed' : 'Unconfirmed'} ${r.affected} pin${r.affected === 1 ? '' : 's'}.`, {
          description: r.warnings.slice(0, 5).join(' · ') + (r.warnings.length > 5 ? ` · +${r.warnings.length - 5} more` : ''),
        });
      } else if (r?.affected > 0) {
        toast.success(`${confirmed ? 'Confirmed' : 'Unconfirmed'} ${r.affected} pin${r.affected === 1 ? '' : 's'}.`);
      }
      await load();
      if (onLinesChanged) await onLinesChanged();
    } catch (e) {
      // Revert the optimistic flip on failure.
      await load();
      toast.error(`Confirm failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, [api, appraisalId, load, onLinesChanged]);

  // Iter 109 — generic per-rate detail editor for Ridge / Barge /
  // Valley / Sheet / Flashing master rate rows. The modal pattern is
  // the same paint-modal shell as the Rescrew detail editor; the
  // child sections call `openRateDetail({ component, rate, pinContext })`
  // to hoist the active rate up here so the modal renders at the parent
  // level (avoids overlay z-index conflicts with nested <details>
  // sections). Iter 110 added the optional `pinContext` argument so
  // per-pin / per-sheet rows can save a structured `detail_override`
  // onto their item without touching the master rate.
  const [detailModal, setDetailModal] = useState({ open: false, component: null, rate: null, pinContext: null });
  const openRateDetail = (component, rate, pinContext = null) => setDetailModal({ open: true, component, rate, pinContext });

  if (!isIronSubtype(subtype)) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-xs text-amber-800" data-testid="iron-not-applicable">
        <AlertCircle size={12} className="inline mr-1" />
        Iron Repairs builder applies to Long-Run iron roofs. Current subtype: <strong>{subtype || '(none)'}</strong>.
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-4 text-xs text-slate-500" data-testid="iron-loading">
        <Loader2 size={11} className="inline animate-spin mr-1" /> Loading Iron Repairs…
      </div>
    );
  }
  if (err) {
    return (
      <div className="bg-rose-50 border border-rose-300 rounded-md p-3 text-xs text-rose-700" data-testid="iron-error">
        <AlertCircle size={12} className="inline mr-1" /> {err}
      </div>
    );
  }
  const cfg = data.config;
  const pendingCounts = data.pending_counts || { ridge: 0, barge: 0, valley: 0, flashing: 0 };
  const totalPending = (pendingCounts.ridge || 0) + (pendingCounts.barge || 0) + (pendingCounts.valley || 0);
  return (
    <div className="space-y-3" data-testid="iron-repairs-section">
      {/* v10.29 Sprint 4b · Yellow banner (2026-02c) — Re-push overwrite
          reminder. Same design DNA as Reroof / Gutter. Per-appraisal
          localStorage dismiss. */}
      {!bannerDismissed && (
        <div className="px-3 py-2 rounded text-xs flex items-start gap-2 bg-amber-50 text-amber-900 ring-1 ring-amber-200"
             data-testid="repairs-repush-banner">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <strong>Heads-up:</strong> Re-pushing repairs overwrites any manual edits made to
            builder-emitted lines in the <span className="font-semibold">Components</span> tab.
          </div>
          <button className="text-[11px] underline hover:no-underline"
                  onClick={dismissBanner}
                  data-testid="repairs-repush-banner-dismiss">
            dismiss (this appraisal)
          </button>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md px-4 py-2.5 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Wrench size={14} className="text-sky-700" /> Iron Repairs — {subtype}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Stage 1: Rescrew, per-pin replacements + flashing upgrades. Stage 2: Paint (water blast · rust treatment · primer · 2× colour).
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Iron sub-total</div>
          <div className="text-lg font-bold text-sky-800 font-mono-num" data-testid="iron-stage1-total">${preview.stage_totals.stage_1.toFixed(2)}</div>
          <div className="text-[10px] text-slate-500">{preview.stage_counts?.stage_1 || 0} line{preview.stage_counts?.stage_1 === 1 ? '' : 's'}</div>
          {/* v10.29 Sprint 4b · Slice A (2026-02c) — Materials/Labour
              split badges. `split_delta` warns when a builder line
              hasn't been updated with per-line mat/lab breakdown. */}
          {(preview.materials_total || preview.labour_total) > 0 && (
            <div className="mt-1 flex flex-wrap justify-end gap-1" data-testid="iron-mat-lab-split">
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white ring-1 ring-slate-200 text-slate-600">
                <span className="font-semibold">Mat:</span> <span className="font-mono-num">${(preview.materials_total || 0).toFixed(2)}</span>
              </span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white ring-1 ring-slate-200 text-slate-600">
                <span className="font-semibold">Lab:</span> <span className="font-mono-num">${(preview.labour_total || 0).toFixed(2)}</span>
              </span>
              {Math.abs(preview.split_delta || 0) >= 0.02 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 ring-1 ring-amber-300 text-amber-800"
                      title="Some lines don't include a mat/lab split — Δ = total − (mat + lab)"
                      data-testid="iron-split-delta-warn">
                  Δ ${(preview.split_delta || 0).toFixed(2)}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Iter216 — global "Pending pins" banner. Shows when any pin
          across Ridge / Barge / Valley / Flashing is unconfirmed. Tells
          the operator that pending rows are excluded from the priced
          total (sub-total above) so the number isn't a surprise.
          One-click "Confirm all" runs the bulk endpoint with section
          = "all". */}
      {totalPending > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-md px-4 py-2.5 flex items-center justify-between gap-3" data-testid="iron-pending-banner">
          <div className="text-xs text-amber-900">
            <strong>⚠ {totalPending} pending pin{totalPending === 1 ? '' : 's'}</strong> waiting for review —
            excluded from the priced total until confirmed.
            <span className="text-amber-700 ml-2">
              {Object.entries(pendingCounts).filter(([s, n]) => n > 0 && s !== 'flashing').map(([s, n]) => `${s} ${n}`).join(' · ')}
            </span>
          </div>
          <button
            type="button"
            data-testid="iron-confirm-all-pending"
            onClick={() => confirmPins('all', null, true)}
            className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold inline-flex items-center gap-1"
          >✓ Confirm all {totalPending} pending</button>
        </div>
      )}

      <RescrewSection api={api} cfg={cfg.rescrew} options={data.options.rescrew} roofM2={data.roof_m2} busy={busy}
                      onChange={(next) => update({ rescrew: next })}
                      onMasterRateUpdated={load} />

      <PinSection title="Ridges & Hips" testidBase="ridge" icon={<GripHorizontal size={13} className="text-sky-700" />}
                  items={cfg.ridge.items} options={data.options.ridge}
                  showRidgeWidth
                  pendingCount={pendingCounts.ridge}
                  onChange={(items) => update({ ridge: { items } })}
                  onJumpToPin={onJumpToPin}
                  onOpenAnnotator={onOpenAnnotator}
                  onConfirmPins={(rawIds, confirmed) => confirmPins('ridge', rawIds, confirmed)}
                  onOpenRateDetail={openRateDetail} />

      <PinSection title="Barges" testidBase="barge" icon={<Triangle size={13} className="text-sky-700" />}
                  items={cfg.barge.items} options={data.options.barge}
                  pendingCount={pendingCounts.barge}
                  onChange={(items) => update({ barge: { items } })}
                  onJumpToPin={onJumpToPin}
                  onOpenAnnotator={onOpenAnnotator}
                  onConfirmPins={(rawIds, confirmed) => confirmPins('barge', rawIds, confirmed)}
                  onOpenRateDetail={openRateDetail} />

      <PinSection title="Valleys" testidBase="valley" icon={<Triangle size={13} className="text-sky-700 rotate-180" />}
                  items={cfg.valley.items} options={data.options.valley}
                  pendingCount={pendingCounts.valley}
                  onChange={(items) => update({ valley: { items } })}
                  onJumpToPin={onJumpToPin}
                  onOpenAnnotator={onOpenAnnotator}
                  onConfirmPins={(rawIds, confirmed) => confirmPins('valley', rawIds, confirmed)}
                  onOpenRateDetail={openRateDetail}
                  showValleyProfile
                  api={api} />

      <SheetSection cfg={cfg.sheet} options={data.options.sheet}
                    onChange={(items) => update({ sheet: { items } })}
                    onOpenAnnotator={onOpenAnnotator}
                    onOpenRateDetail={openRateDetail} />

      <FlashingSection items={cfg.flashing.items} api={api} appraisalId={appraisalId} />

      {/* Iter211 — Stage-2 paint section. Wrapped in a toggle when the
          parent indicates a Repairs-only context so the Paint Systems
          form isn't visible by default for a pure Repairs job (you'd
          only want it for a Repairs+Paint package — handled by the
          Restoration tab). */}
      {paintHiddenByDefault && !paintRevealed ? (
        <button
          type="button"
          data-testid="iron-repairs-reveal-paint"
          onClick={() => setPaintRevealed(true)}
          className="w-full px-4 py-3 border-2 border-dashed border-emerald-300 bg-emerald-50/40 hover:bg-emerald-50 text-emerald-800 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
          title="Open the Stage 2 — Paint Systems picker for this Repairs job"
        >
          🎨 + Select painting info
          <span className="text-[11px] font-normal text-emerald-700/70 ml-1">(Stage 2 paint hidden by default for Repairs)</span>
        </button>
      ) : (
        <Stage2PaintSection api={api} appraisalId={appraisalId} onLinesChanged={load} />
      )}

      <CalculatedLinesPanel preview={preview} showCalcs={showCalcs} toggleCalcs={toggleCalcs} jumpToComponents={jumpToComponents} />

      <IronRateDetailModal
        api={api}
        open={detailModal.open}
        component={detailModal.component}
        rate={detailModal.rate}
        pinContext={detailModal.pinContext}
        onOpenChange={(o) => setDetailModal(m => ({ ...m, open: o }))}
        onSave={async () => { await load(); }}
      />
    </div>
  );
}


// ── Rescrew ───────────────────────────────────────────────────────────


function RescrewSection({ api, cfg, options, roofM2, busy, onChange, onMasterRateUpdated }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const inheritedFromRoof = cfg.area_m2_override == null;
  const effArea = inheritedFromRoof ? roofM2 : Number(cfg.area_m2_override || 0);
  const rate = options.find(o => o.id === cfg.rate_id) || options[0];
  // Iter 106 — effective values merge the appraisal-scoped
  // detail_override block over the master-rate columns so the
  // breakdown row shows what the calculator will actually use.
  const ov = cfg.detail_override || {};
  const effRate = rate ? {
    work_type:      ov.work_type      || rate.work_type      || rate.name || '',
    screw_size:     ov.screw_size     || rate.screw_size     || '',
    rate_basis:     rate.rate_basis   || '/m²',
    materials_desc: ov.materials_desc || rate.materials_desc || '',
    supplier_code:  ov.supplier_code  || rate.supplier_code  || '',
    buy_cost:       ov.buy_cost       ?? rate.buy_per_lm     ?? 0,
    qty_per_m2:     ov.qty_per_m2     ?? rate.qty_per_m2     ?? 7,
    materials_rate: ov.materials_rate ?? rate.material_per_lm?? 0,
    labour_rate:    ov.labour_rate    ?? rate.labour_per_lm  ?? 0,
    contractor_rate:ov.contractor_rate?? rate.contractor_per_lm ?? 0,
  } : null;
  const effChargeRate = effRate ? (Number(effRate.materials_rate || 0) + Number(effRate.labour_rate || 0)) : 0;
  const lineTotal = (cfg.enabled && effRate && effArea > 0) ? (effArea * effChargeRate) : 0;
  const hasOverride = Object.keys(ov || {}).length > 0;

  const handleDetailSave = async ({ override, masterUpdated }) => {
    // Save the appraisal-level override into the rescrew config —
    // parent component will persist via the existing save endpoint.
    onChange({ ...cfg, detail_override: override });
    if (masterUpdated && onMasterRateUpdated) {
      // Reload the rate options so the master-rate edits surface.
      await onMasterRateUpdated();
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="rescrew-section">
      <div className="px-4 py-2.5 border-b border-slate-200 bg-gradient-to-r from-sky-50 to-cyan-50 flex items-center justify-between gap-3 flex-wrap">
        <label className="text-sm font-bold text-slate-800 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled} data-testid="rescrew-enabled"
                 onChange={(e) => onChange({ ...cfg, enabled: e.target.checked, rate_id: cfg.rate_id || (options[0]?.id || '') })}
                 className="rounded text-sky-600 focus:ring-sky-400" />
          Rescrew (replace nails with tek screws)
        </label>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
          <div className="text-base font-bold text-sky-800 font-mono-num" data-testid="rescrew-total">${lineTotal.toFixed(2)}</div>
        </div>
      </div>
      {cfg.enabled && (
        <div className="p-4 grid grid-cols-12 gap-3 text-xs">
          <label className="col-span-5 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center justify-between gap-2">
              <span>Rate row</span>
              {rate && <EditRateLink component="fastening" rateId={rate.id} testid="rescrew-rate-edit" />}
            </span>
            {/* Iter211 — default selection prefers the Tek 12×65 rate
                (most common rescrew). Falls back to first option when
                no name matches. */}
            <select value={cfg.rate_id || (options.find(o => /12\s*[x*×]\s*65/i.test(o.name || ''))?.id) || (options[0]?.id || '')} disabled={busy}
                    data-testid="rescrew-rate-id"
                    onChange={(e) => onChange({ ...cfg, rate_id: e.target.value, detail_override: {} })}
                    className="px-2 py-1 text-xs border border-slate-300 rounded">
              {options.length === 0 && <option value="">No Rescrew rate rows found</option>}
              {options.map(o => <option key={o.id} value={o.id}>{o.name} · ${o.rate_per_lm.toFixed(2)}/m²</option>)}
            </select>
          </label>
          <label className="col-span-3 flex flex-col gap-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Area (m²) {inheritedFromRoof && <span className="text-emerald-600 normal-case font-normal" title="Auto-linked from Roof Details">· auto</span>}
            </span>
            <div className="flex items-center gap-1">
              <input type="number" step="0.1" min="0"
                     value={cfg.area_m2_override ?? ''}
                     placeholder={roofM2 > 0 ? `auto: ${roofM2.toFixed(1)}` : '0'}
                     data-testid="rescrew-area-override"
                     onChange={(e) => {
                       const v = e.target.value === '' ? null : parseFloat(e.target.value);
                       onChange({ ...cfg, area_m2_override: v });
                     }}
                     className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
              {cfg.area_m2_override != null && (
                <button type="button" data-testid="rescrew-area-reset"
                        onClick={() => onChange({ ...cfg, area_m2_override: null })}
                        className="text-[10px] text-slate-500 hover:text-sky-700 underline">reset</button>
              )}
            </div>
          </label>
          <div className="col-span-4 text-xs text-slate-600 flex items-end">
            <div className="bg-sky-50 border border-sky-200 rounded px-2 py-1 w-full">
              {effRate && effArea > 0
                ? <>{effArea.toFixed(1)} m² × <strong>${effChargeRate.toFixed(2)}/m²</strong> = <strong className="text-sky-800">${lineTotal.toFixed(2)}</strong></>
                : <span className="text-slate-400 italic">pick a rate row + roof area</span>}
            </div>
          </div>

          {/* Iter 106 — breakdown card. Splits the rate-row info into
              columns the operator can see at a glance, with a pencil
              that opens the detail edit modal. */}
          {effRate && (
            <div className="col-span-12 mt-1 border border-slate-200 rounded overflow-hidden bg-slate-50" data-testid="rescrew-breakdown">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-100 border-b border-slate-200">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-2 py-1 font-bold">Work Type</th>
                    <th className="px-2 py-1 font-bold">Screw Size</th>
                    <th className="px-2 py-1 font-bold">Qty/m²</th>
                    <th className="px-2 py-1 font-bold">Tekscrew $</th>
                    <th className="px-2 py-1 font-bold">Code</th>
                    <th className="px-2 py-1 font-bold">Materials $/m²</th>
                    <th className="px-2 py-1 font-bold">Labour $/m²</th>
                    <th className="px-2 py-1 font-bold text-right">Charge $/m²</th>
                    <th className="px-2 py-1 font-bold text-right">Contractor $/m²</th>
                    <th className="px-2 py-1 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={hasOverride ? 'bg-amber-50' : ''} data-testid="rescrew-breakdown-row">
                    <td className="px-2 py-1 font-semibold text-slate-800" data-testid="rescrew-breakdown-work-type">{effRate.work_type || '—'}</td>
                    <td className="px-2 py-1 font-mono text-slate-700" data-testid="rescrew-breakdown-screw-size">{effRate.screw_size || '—'}</td>
                    <td className="px-2 py-1 font-mono-num text-slate-700" data-testid="rescrew-breakdown-qty-per-m2">{Number(effRate.qty_per_m2 || 0).toFixed(0)}</td>
                    <td className="px-2 py-1 font-mono-num text-slate-700" data-testid="rescrew-breakdown-buy-cost">${Number(effRate.buy_cost || 0).toFixed(2)} ea</td>
                    <td className="px-2 py-1 font-mono text-[10px] text-slate-500" data-testid="rescrew-breakdown-code" title={effRate.materials_desc || ''}>{effRate.supplier_code || (effRate.materials_desc ? effRate.materials_desc.match(/\(([^)]+)\)/)?.[1] || '—' : '—')}</td>
                    <td className="px-2 py-1 font-mono-num text-slate-700" data-testid="rescrew-breakdown-materials-rate">${Number(effRate.materials_rate || 0).toFixed(2)}</td>
                    <td className="px-2 py-1 font-mono-num text-slate-700" data-testid="rescrew-breakdown-labour-rate">${Number(effRate.labour_rate || 0).toFixed(2)}</td>
                    <td className="px-2 py-1 font-mono-num font-bold text-sky-800 text-right" data-testid="rescrew-breakdown-charge-rate">${effChargeRate.toFixed(2)}</td>
                    <td className="px-2 py-1 font-mono-num text-slate-700 text-right" data-testid="rescrew-breakdown-contractor-rate">${Number(effRate.contractor_rate || 0).toFixed(2)}</td>
                    <td className="px-2 py-1 text-right">
                      <button type="button"
                              data-testid="rescrew-breakdown-edit"
                              onClick={() => setDetailOpen(true)}
                              title="Edit rescrew detail (override or master)"
                              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold text-[#D35400] hover:text-[#A04000] hover:bg-orange-50 rounded border border-[#D35400]">
                        <Pencil size={9} /> Edit
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              {hasOverride && (
                <div className="px-2 py-1 text-[10px] text-amber-800 bg-amber-100 border-t border-amber-200 flex items-center gap-1.5" data-testid="rescrew-breakdown-override-banner">
                  <AlertCircle size={10} /> This appraisal is using a custom override. Master rate row unchanged.
                  <button type="button" data-testid="rescrew-breakdown-clear-override"
                          onClick={() => onChange({ ...cfg, detail_override: {} })}
                          className="ml-auto text-amber-900 underline hover:text-amber-950">Clear override</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <RescrewDetailModal
        api={api}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        rate={rate}
        override={cfg.detail_override}
        onSave={handleDetailSave}
      />
    </div>
  );
}


// ── Per-pin section (Ridges / Barges / Valleys) ───────────────────────

// Iter 127 — Material classifier for rate-row names. Lets the operator
// pick a material (Zincalume / Galvanised / MagnaFlow / Maxam / Maxx /
// ZCore / Copper) instead of having to scan the full rate-row list.
const RIDGE_MATERIALS = [
  { key: 'zincalume',  label: 'Zincalume',         match: /\bzinc(alume)?\b/i },
  { key: 'galvanised', label: 'Galvanised',        match: /\bgalv/i },
  { key: 'magnaflow',  label: 'MagnaFlow',         match: /magnaflow/i },
  { key: 'maxam',      label: 'Maxam',             match: /\bmaxam\b/i },
  { key: 'maxx',       label: 'ColorSteel MaxX',   match: /\bmaxx\b/i },
  { key: 'zcore',      label: 'ColorSteel ZCore',  match: /\bzcore\b|\bzinacore\b/i },
  { key: 'copper',     label: 'Copper',            match: /\bcopper\b/i },
];

function detectMaterial(name) {
  if (!name) return null;
  for (const m of RIDGE_MATERIALS) if (m.match.test(name)) return m;
  return null;
}

// Reduce the catalogue down to one option per material (first match
// wins — operator can still tweak the underlying rate row via the
// Detail / EditRateLink buttons). Returns ordered list keyed by
// `RIDGE_MATERIALS` so the dropdown order is stable.
function groupOptionsByMaterial(options) {
  const seen = new Map();
  for (const o of options || []) {
    const m = detectMaterial(o.name);
    if (!m) continue;
    if (!seen.has(m.key)) seen.set(m.key, { material: m, option: o });
  }
  return RIDGE_MATERIALS
    .filter(m => seen.has(m.key))
    .map(m => seen.get(m.key));
}

// Iron ridging comes in Std and Wide Roundtop widths ("Wide Ridge …").
const isWideRidge = (name) => /^\s*wide\b/i.test(name || '');
function itemIsWide(item, options) {
  const o = options.find(x => x.id === item.rate_id);
  if (o) return isWideRidge(o.name);
  if (item.ridge_width) return item.ridge_width === 'wide';
  return /wide/i.test(item.ridge_cap_type || '');
}


function PinSection({ title, testidBase, icon, items, options, pendingCount = 0, onChange, onJumpToPin, onOpenAnnotator, onConfirmPins, onOpenRateDetail, showValleyProfile = false, showRidgeWidth = false, api = null }) {
  // Iter230c (A5027) — `partial` action contributes to the active
  // set alongside `replace` for material/cost rollup purposes.
  const replacing = items.filter(i => i.action === 'replace' || i.action === 'partial');
  // Iter211 — Valley Profile options. Fetched from the existing
  // `dropdown_options.valley_profile` category so the office can extend
  // the list via /dropdown-manager without a code change. Falls back to
  // the 3 hardcoded NZ-standard profiles if the API call hasn't
  // returned yet (or returns empty).
  const VALLEY_PROFILE_FALLBACK = ['15/135/135/15', '15/15/135/135/15/15', '25/180/180/25'];
  const [VALLEY_PROFILES, setValleyProfiles] = useState(VALLEY_PROFILE_FALLBACK);
  useEffect(() => {
    if (!showValleyProfile || !api) return;
    let cancelled = false;
    api.get('/dropdowns/valley_profile').then((r) => {
      if (cancelled) return;
      const arr = Array.isArray(r.data) ? r.data.filter(Boolean) : [];
      if (arr.length > 0) setValleyProfiles(arr);
    }).catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, [api, showValleyProfile]);
  // Derive section-level default from majority pick among rows; falls
  // back to the first profile when nothing is set.
  const sectionValleyProfile = useMemo(() => {
    if (!showValleyProfile) return '';
    const counts = new Map();
    for (const it of items) {
      const vp = (it.valley_profile || '').trim();
      if (!vp) continue;
      counts.set(vp, (counts.get(vp) || 0) + 1);
    }
    let best = '', bestN = 0;
    for (const [k, n] of counts) { if (n > bestN) { best = k; bestN = n; } }
    return best;
  }, [items, showValleyProfile]);
  const applyValleyProfileToAll = (profile) => {
    if (!profile) return;
    onChange(items.map(it => ({ ...it, valley_profile: profile })));
  };
  // Iter211 — derived flags used by both the bulk-action button colours
  // AND the auto-collapse default. `allLeave` = every pin marked as
  // "leave" → operator has nothing to configure, so collapse the
  // section by default to reclaim screen real-estate.
  const allLeave   = items.length > 0 && replacing.length === 0;
  const allReplace = items.length > 0 && replacing.length === items.length;
  // Track whether the operator manually toggled <details> open. Once
  // they do, their choice sticks (we never override) so we don't fight
  // them on every re-render.
  const [userOpen, setUserOpen] = useState(null);
  const effectiveOpen = userOpen === null ? !allLeave : userOpen;
  const sectionTotal = useMemo(() => {
    return replacing.reduce((acc, i) => {
      const opt = options.find(o => o.id === i.rate_id);
      const rate = i.rate_override != null && i.rate_override !== ''
        ? Number(i.rate_override)
        : (opt ? opt.rate_per_lm : 0);
      return acc + (Number(i.length_m || 0) * rate);
    }, 0);
  }, [replacing, options]);
  const updateItem = (idx, patch) => {
    const next = items.map((it, j) => j === idx ? { ...it, ...patch } : it);
    onChange(next);
  };
  // Iter 127 — bulk actions.
  const materialOpts = useMemo(() => groupOptionsByMaterial(options), [options]);
  const materialOptsByWidth = useMemo(() => ({
    std:  groupOptionsByMaterial(options.filter(o => !isWideRidge(o.name))),
    wide: groupOptionsByMaterial(options.filter(o => isWideRidge(o.name))),
  }), [options]);
  const matOptsFor = (it) => (showRidgeWidth
    ? materialOptsByWidth[itemIsWide(it, options) ? 'wide' : 'std']
    : materialOpts);
  const setAllAction = (act) => {
    // Iter211 — when bulk-flipping Valleys to "replace", default the
    // material to Galvanised (the trade default for valley replacements
    // on Iron roofs). Only applied when there's no current dominant
    // material on the section, so we never overwrite an operator's
    // prior material pick.
    let next = items.map(it => ({ ...it, action: act }));
    if (act === 'replace' && testidBase === 'valley') {
      const galv = materialOpts.find(m => m.material.key === 'galvanised');
      if (galv) {
        next = next.map(it => (
          it.action === 'replace' && !it.rate_id
            ? { ...it, rate_id: galv.option.id }
            : it
        ));
      }
    }
    onChange(next);
  };
  const setAllMaterial = (matKey) => {
    if (!matKey) return;
    onChange(items.map(it => {
      if (it.action !== 'replace') return it;
      const entry = matOptsFor(it).find(m => m.material.key === matKey);
      return entry ? { ...it, rate_id: entry.option.id } : it;
    }));
  };
  // Detect the dominant material from currently-selected rate rows
  // (most-common). Falls back to first material option.
  const dominantMaterial = useMemo(() => {
    const counts = new Map();
    for (const it of replacing) {
      const opt = options.find(o => o.id === it.rate_id);
      const m = detectMaterial(opt?.name || '');
      if (m) counts.set(m.key, (counts.get(m.key) || 0) + 1);
    }
    let best = null;
    let bestN = 0;
    for (const [k, n] of counts) { if (n > bestN) { best = k; bestN = n; } }
    return best;
  }, [replacing, options]);
  return (
    <details
      className="bg-white border border-slate-200 rounded-md"
      open={effectiveOpen}
      onToggle={(e) => setUserOpen(e.currentTarget.open)}
      data-testid={`${testidBase}-section`}
    >
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          {icon} {title}
          <span className="text-xs font-normal text-slate-500">
            ({items.length} pin{items.length === 1 ? '' : 's'} · {replacing.length} replacing)
          </span>
          {/* Iter211 — explicit "All left" chip on the summary line so the
              collapsed state is self-explanatory; saves the operator
              having to expand the section just to check status. */}
          {allLeave && items.length > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded ring-1 ring-sky-200">
              ✓ All left
            </span>
          )}
          {/* Iter216 — per-section pending count badge. Visible even when
              the section is collapsed so the operator can see at a
              glance there's review work to do. */}
          {pendingCount > 0 && (
            <span data-testid={`${testidBase}-pending-badge`} className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded ring-1 ring-amber-300">
              ⚠ {pendingCount} pending
            </span>
          )}
        </span>
        <span className="font-mono-num text-sky-800 font-bold" data-testid={`${testidBase}-total`}>${sectionTotal.toFixed(2)}</span>
      </summary>
      {/* Iter215 — Edit-on-diagram button rendered just inside the
          collapsible body so it sits beneath the section header but
          remains tied to this section context. Operator clicks it →
          full-screen annotator pops open scoped to this pin type. */}
      {(onOpenAnnotator || (pendingCount > 0 && onConfirmPins)) && (
        <div className="px-4 -mb-1 flex items-center gap-2 flex-wrap">
          {onOpenAnnotator && (
            <button
              type="button"
              data-testid={`${testidBase}-open-annotator`}
              onClick={(e) => { e.stopPropagation(); onOpenAnnotator(title); }}
              className="text-[11px] px-2 py-0.5 border border-[#D35400] text-[#D35400] bg-orange-50 hover:bg-[#D35400] hover:text-white rounded font-semibold transition-colors inline-flex items-center gap-1"
              title={`Open the roof-plan annotator to edit ${title.toLowerCase()} on the diagram. Closes back to here.`}
            ><Pencil size={10} /> Edit {title.toLowerCase()} on diagram ↗</button>
          )}
          {/* Iter216 — "Confirm all N pending" button only renders when
              the section has unconfirmed pins. Runs the bulk endpoint
              scoped to this section. Reversible — operator can later
              unconfirm individual pins via the per-row chip. */}
          {pendingCount > 0 && onConfirmPins && (
            <button
              type="button"
              data-testid={`${testidBase}-confirm-all`}
              onClick={(e) => { e.stopPropagation(); onConfirmPins(null, true); }}
              className="text-[11px] px-2 py-0.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-bold inline-flex items-center gap-1 shadow-sm"
              title={`Confirm all ${pendingCount} pending ${title.toLowerCase()} pin${pendingCount === 1 ? '' : 's'} as Replace`}
            >✓ Confirm all {pendingCount} pending</button>
          )}
        </div>
      )}
      <div className="px-4 pb-3">
        {items.length === 0 ? (
          <div className="text-xs text-slate-500 italic py-2">No {title.toLowerCase()} pins on the roof diagram yet.</div>
        ) : (
          <>
            {/* Iter 127 — bulk actions: select-all + material picker.
                Iter211 — active state colour-codes: green when "Replace
                all" is active (all rows replacing), blue when "Leave all"
                is active (all rows left). Operator can see the bulk
                choice at a glance even in the table view. */}
            <div className="flex flex-wrap items-center gap-2 mb-2 text-[11px] bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
              <span className="font-semibold text-slate-600">Bulk:</span>
              <button
                type="button"
                data-testid={`${testidBase}-bulk-replace-all`}
                onClick={() => setAllAction('replace')}
                className={`px-2 py-0.5 border rounded font-semibold transition-colors ${
                  allReplace
                    ? 'border-emerald-600 bg-emerald-600 text-white shadow-sm'
                    : 'border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                }`}
              >
                Replace all
              </button>
              <button
                type="button"
                data-testid={`${testidBase}-bulk-leave-all`}
                onClick={() => setAllAction('leave')}
                className={`px-2 py-0.5 border rounded font-semibold transition-colors ${
                  allLeave
                    ? 'border-sky-600 bg-sky-600 text-white shadow-sm'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                Leave all
              </button>
              {materialOpts.length > 0 && (
                <>
                  <span className="text-slate-500 ml-2">Material for replacements:</span>
                  <select value={dominantMaterial || ''} data-testid={`${testidBase}-bulk-material`}
                          onChange={(e) => setAllMaterial(e.target.value)}
                          className="px-1.5 py-0.5 text-xs border border-slate-300 rounded bg-white">
                    <option value="">— pick material —</option>
                    {materialOpts.map(({ material, option }) => {
                      const wide = showRidgeWidth && materialOptsByWidth.wide.find(m => m.material.key === material.key);
                      return (
                        <option key={material.key} value={material.key}>
                          {material.label} · ${option.rate_per_lm.toFixed(2)}{wide ? ` / Wide $${wide.option.rate_per_lm.toFixed(2)}` : ''}
                        </option>
                      );
                    })}
                  </select>
                </>
              )}
              {/* Iter211 — section-level Valley Profile picker. Sets every
                  row's `valley_profile` in one click; per-row override
                  still available in the table below. */}
              {showValleyProfile && (
                <>
                  <span className="text-slate-500 ml-2">Valley profile:</span>
                  <select value={sectionValleyProfile} data-testid={`${testidBase}-bulk-valley-profile`}
                          onChange={(e) => applyValleyProfileToAll(e.target.value)}
                          className="px-1.5 py-0.5 text-xs border border-slate-300 rounded bg-white font-mono-num">
                    <option value="">— pick profile —</option>
                    {VALLEY_PROFILES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </>
              )}
            </div>
          <div className="space-y-2" data-testid={`${testidBase}-rows`}>
            {items.map((i, idx) => {
                const opt = options.find(o => o.id === i.rate_id);
                const rate = i.rate_override != null && i.rate_override !== ''
                  ? Number(i.rate_override)
                  : (opt ? opt.rate_per_lm : 0);
                // Iter230c — partial replacement clamping.
                const pinLen = Number(i.length_m || 0);
                const partialLen = (() => {
                  const raw = i.replace_length_m;
                  if (raw === undefined || raw === null || raw === '') return pinLen;
                  const f = Number(raw);
                  if (!Number.isFinite(f)) return pinLen;
                  return Math.max(0, Math.min(pinLen, f));
                })();
                const effLen = i.action === 'partial' ? partialLen : pinLen;
                const total = (i.action === 'replace' || i.action === 'partial')
                  ? (effLen * rate)
                  : 0;
                const isPending = !i.confirmed;
                const isActive = i.action === 'replace' || i.action === 'partial';
                const hasOverride = Object.keys(i.detail_override || {}).length > 0;
                const currentOpt = options.find(o => o.id === i.rate_id);
                const currentMaterial = detectMaterial(currentOpt?.name || '')?.key || '';
                const rowMatOpts = matOptsFor(i);
                const rowWide = showRidgeWidth && itemIsWide(i, options);
                const setRowWidth = (wide) => {
                  const entry = materialOptsByWidth[wide ? 'wide' : 'std'].find(m => m.material.key === currentMaterial);
                  updateItem(idx, {
                    ridge_width: wide ? 'wide' : 'std',
                    rate_id: entry ? entry.option.id : '',
                    detail_override: {},
                  });
                };
                return (
                  <div key={i.raw_id || i.pin_id || idx}
                       data-testid={`${testidBase}-row-${idx}`}
                       data-pending={isPending ? '1' : '0'}
                       className={`rounded-md border px-3 py-2 ${isPending ? 'bg-amber-50/40 border-amber-200' : 'bg-white border-sky-200'}`}>
                    {/* Row header: pin chip + confirm + source badge + total */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span data-testid={`${testidBase}-pin-${idx}`}
                            onClick={onJumpToPin ? () => onJumpToPin(i.raw_id || i.pin_id) : undefined}
                            className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-[10px] font-bold text-white ring-1 ring-sky-800/40 ${onJumpToPin ? 'cursor-pointer hover:ring-sky-900' : ''} ${isPending ? 'bg-amber-500' : 'bg-sky-700'}`}
                            title={onJumpToPin ? 'Click to scroll the diagram to this pin' : i.pin_id}>
                        {i.pin_id}
                      </span>
                      {onConfirmPins && (
                        isPending ? (
                          <button type="button"
                                  data-testid={`${testidBase}-confirm-${idx}`}
                                  onClick={() => onConfirmPins([i.raw_id], true)}
                                  className="text-[9px] px-1.5 py-0.5 bg-amber-400 hover:bg-emerald-600 hover:text-white text-amber-900 rounded-full font-bold uppercase tracking-wider ring-1 ring-amber-500"
                                  title="Pending review — click to confirm">⚠ Pending</button>
                        ) : (
                          <button type="button"
                                  data-testid={`${testidBase}-unconfirm-${idx}`}
                                  onClick={() => onConfirmPins([i.raw_id], false)}
                                  className="text-[9px] px-1.5 py-0.5 bg-emerald-100 hover:bg-amber-100 text-emerald-700 hover:text-amber-800 rounded-full font-bold uppercase tracking-wider ring-1 ring-emerald-300 opacity-60 hover:opacity-100"
                                  title="Confirmed — click to unconfirm">✓</button>
                        )
                      )}
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Diagram</span>
                      {String(i.pin_type || '').toLowerCase() === 'hip' && (
                        <span data-testid={`${testidBase}-hip-badge-${idx}`}
                              className="text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200">Hip</span>
                      )}
                      <span className="text-[10px] font-mono-num text-slate-500">{pinLen.toFixed(2)} m</span>
                      <div className="ml-auto text-right">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
                        <div className="text-sm font-bold text-sky-800 font-mono-num">${total.toFixed(2)}</div>
                      </div>
                    </div>
                    {/* Controls grid */}
                    <div className="mt-2 grid grid-cols-12 gap-2 items-end">
                      {/* Action */}
                      <label className="col-span-6 md:col-span-2 flex flex-col gap-0.5">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Action</span>
                        <select value={i.action || 'leave'} data-testid={`${testidBase}-action-${idx}`}
                                onChange={(e) => updateItem(idx, { action: e.target.value })}
                                onDoubleClick={() => updateItem(idx, {
                                  action: ({ leave: 'replace', replace: 'partial', partial: 'leave' })[i.action || 'leave'] || 'replace',
                                })}
                                title="Double-click to cycle Leave → Replace → Partial"
                                className="px-2 py-1 text-xs border border-slate-300 rounded bg-white">
                          <option value="leave">Leave</option>
                          <option value="replace">Replace</option>
                          <option value="partial">Partial</option>
                        </select>
                      </label>
                      {/* Partial length (only when partial) */}
                      {i.action === 'partial' && (
                        <label className="col-span-6 md:col-span-2 flex flex-col gap-0.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Replace length</span>
                          <div className="flex items-center gap-1">
                            <input type="number" step="0.01" min="0" max={pinLen}
                                   data-testid={`${testidBase}-partial-length-${idx}`}
                                   value={i.replace_length_m ?? ''}
                                   placeholder={pinLen.toFixed(2)}
                                   onChange={(e) => updateItem(idx, { replace_length_m: e.target.value })}
                                   className="flex-1 px-2 py-1 text-xs border border-amber-300 rounded bg-amber-50 font-mono-num"
                                   title={`Max ${pinLen.toFixed(2)} m`} />
                            <span className="text-[10px] text-slate-500 font-mono-num">/{pinLen.toFixed(1)}</span>
                          </div>
                        </label>
                      )}
                      {/* Ridge width (Std / Wide Roundtop) */}
                      {showRidgeWidth && isActive && (
                        <label className="col-span-6 md:col-span-2 flex flex-col gap-0.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Ridge type</span>
                          <select value={rowWide ? 'wide' : 'std'}
                                  data-testid={`${testidBase}-width-${idx}`}
                                  onChange={(e) => setRowWidth(e.target.value === 'wide')}
                                  className="px-2 py-1 text-xs border border-slate-300 rounded bg-white">
                            <option value="std">Std Roundtop</option>
                            <option value="wide">Wide Roundtop</option>
                          </select>
                        </label>
                      )}
                      {/* Material (when replacing) */}
                      {isActive && (
                        <label className="col-span-12 md:col-span-3 flex flex-col gap-0.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Replace material</span>
                          <select value={currentMaterial}
                                  data-testid={`${testidBase}-material-${idx}`}
                                  onChange={(e) => {
                                    const entry = rowMatOpts.find(m => m.material.key === e.target.value);
                                    updateItem(idx, { rate_id: entry ? entry.option.id : '' });
                                  }}
                                  title={currentOpt ? `Rate row: ${currentOpt.name}` : 'Pick a replacement material'}
                                  className="px-2 py-1 text-xs border border-slate-300 rounded bg-white">
                            <option value="">— pick material —</option>
                            {rowMatOpts.map(({ material, option }) => (
                              <option key={material.key} value={material.key}>
                                {material.label} · ${option.rate_per_lm.toFixed(2)}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      {/* Valley profile (when applicable + replacing) */}
                      {showValleyProfile && isActive && (
                        <label className="col-span-6 md:col-span-2 flex flex-col gap-0.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Profile</span>
                          <select value={i.valley_profile || sectionValleyProfile || ''}
                                  data-testid={`${testidBase}-profile-${idx}`}
                                  onChange={(e) => updateItem(idx, { valley_profile: e.target.value })}
                                  className="px-2 py-1 text-xs border border-slate-300 rounded bg-white font-mono-num">
                            <option value="">—</option>
                            {VALLEY_PROFILES.map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </label>
                      )}
                      {/* Rate override (when replacing) */}
                      {isActive && (
                        <label className="col-span-6 md:col-span-2 flex flex-col gap-0.5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">$/lm override</span>
                          <input type="number" step="0.01" min="0"
                                 value={i.rate_override ?? ''}
                                 placeholder={opt ? opt.rate_per_lm.toFixed(2) : '—'}
                                 data-testid={`${testidBase}-rate-override-${idx}`}
                                 onChange={(e) => updateItem(idx, { rate_override: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                 className="px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
                        </label>
                      )}
                      {/* Detail + Edit-rate icons (when replacing + rate chosen) */}
                      {isActive && i.rate_id && opt && (
                        <div className="col-span-12 md:col-span-1 flex items-center justify-end gap-1">
                          {onOpenRateDetail && (
                            <button type="button"
                                    data-testid={`${testidBase}-rate-detail-${idx}`}
                                    onClick={() => onOpenRateDetail(testidBase, opt, {
                                      label: `${title.slice(0, -1)} pin ${i.pin_id}`,
                                      detailOverride: i.detail_override || {},
                                      onSaveOverride: (override) => updateItem(idx, { detail_override: override }),
                                    })}
                                    title={hasOverride ? 'Edit per-pin override' : 'Edit master rate detail'}
                                    className={`inline-flex items-center gap-0.5 px-1.5 py-1 text-[10px] font-semibold rounded border ${
                                      hasOverride
                                        ? 'text-amber-700 hover:text-amber-900 hover:bg-amber-50 border-amber-300 bg-amber-50/60'
                                        : 'text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 border-emerald-300'
                                    }`}>
                              <Pencil size={9} /> {hasOverride ? 'OV' : 'Det'}
                            </button>
                          )}
                          <EditRateLink component={testidBase} rateId={i.rate_id} testid={`${testidBase}-rate-edit-${idx}`} label="✎" />
                        </div>
                      )}
                      {!isActive && (
                        <div className="col-span-6 md:col-span-9 flex items-center">
                          <span className="text-slate-400 italic text-[11px]">leave — no replacement</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
          </>
        )}
      </div>
    </details>
  );
}


// ── Sheets (operator-added rows) ──────────────────────────────────────


function SheetSection({ cfg, options, onChange, onOpenRateDetail }) {
  const items = cfg.items || [];
  const total = useMemo(() => items.reduce((acc, i) => {
    const opt = options.find(o => o.id === i.rate_id);
    const rate = i.rate_override != null && i.rate_override !== ''
      ? Number(i.rate_override)
      : (opt ? opt.rate_per_lm : 0);
    return acc + (Number(i.qty || 0) * Number(i.length_m || 0) * rate);
  }, 0), [items, options]);
  const updateItem = (idx, patch) => onChange(items.map((it, j) => j === idx ? { ...it, ...patch } : it));
  // Iter211 — copy rate_id from the prior row so multi-sheet jobs
  // don't force the operator to re-pick the same Profile + Material.
  const addRow = () => {
    const lastRow = items.length > 0 ? items[items.length - 1] : null;
    const defaultRateId = lastRow?.rate_id || options[0]?.id || '';
    onChange([...items, {
      id: crypto.randomUUID(),
      label: '',
      rate_id: defaultRateId,
      qty: 1,
      length_m: 0,
      rate_override: null,
    }]);
  };
  const removeRow = (idx) => onChange(items.filter((_, j) => j !== idx));
  return (
    <details className="bg-white border border-slate-200 rounded-md" open data-testid="sheet-section">
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2"><Pipette size={13} className="text-sky-700" /> Sheets <span className="text-xs font-normal text-slate-500">({items.length} row{items.length === 1 ? '' : 's'})</span></span>
        <span className="font-mono-num text-sky-800 font-bold" data-testid="sheet-total">${total.toFixed(2)}</span>
      </summary>
      <div className="px-4 pb-3 space-y-2">
        {items.length === 0 ? (
          <div className="text-xs text-slate-500 italic py-2">No sheet replacements yet. Click &ldquo;Add sheet line&rdquo; to start.</div>
        ) : items.map((i, idx) => {
          // Iter261 — card-styled row mirroring FlashingRepairCardRow's
          // visual language: S-chip on the left, profile cascade
          // dropdown, qty/length trio, rate override + total + trash.
          const opt = options.find(o => o.id === i.rate_id);
          const rate = i.rate_override != null && i.rate_override !== ''
            ? Number(i.rate_override) : (opt ? opt.rate_per_lm : 0);
          const totalLm = Number(i.qty || 0) * Number(i.length_m || 0);
          const lineTotal = totalLm * rate;
          const hasOverride = Object.keys(i.detail_override || {}).length > 0;
          return (
            <div key={i.id || idx}
                 data-testid={`sheet-row-${idx}`}
                 className="bg-white border border-sky-200 rounded-md shadow-sm px-3 py-2">
              <div className="flex items-center gap-2 flex-wrap">
                {/* S-chip — sheet palette colour, same shape/size as F-chip */}
                <span
                  data-testid={`sheet-chip-${idx}`}
                  className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-[10px] font-bold text-white bg-sky-700 ring-1 ring-sky-800/40"
                  title="Sheet line"
                >S{idx + 1}</span>
                <input type="text" value={i.label || ''} placeholder="optional label (e.g. main roof)"
                       data-testid={`sheet-label-${idx}`}
                       onChange={(e) => updateItem(idx, { label: e.target.value })}
                       className="flex-1 min-w-[160px] max-w-[260px] px-2 py-1 text-xs border border-slate-300 rounded" />
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Manual</span>
                <div className="ml-auto text-right">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Total</div>
                  <div className="text-sm font-bold text-sky-800 font-mono-num" data-testid={`sheet-total-${idx}`}>
                    ${lineTotal.toFixed(2)}
                  </div>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-12 gap-2 items-end">
                {/* Profile picker — col-span 6 */}
                <label className="col-span-12 md:col-span-6 flex flex-col gap-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Profile</span>
                  <div className="flex items-center gap-1">
                    <select value={i.rate_id || ''} data-testid={`sheet-rate-${idx}`}
                            onChange={(e) => updateItem(idx, { rate_id: e.target.value })}
                            className="flex-1 px-2 py-1 text-xs border border-slate-300 rounded bg-white">
                      <option value="">— pick profile —</option>
                      {options.map(o => <option key={o.id} value={o.id}>{o.name} · ${o.rate_per_lm.toFixed(2)}/m</option>)}
                    </select>
                    {i.rate_id && opt && onOpenRateDetail && (
                      <button type="button"
                              data-testid={`sheet-rate-detail-${idx}`}
                              onClick={() => onOpenRateDetail('sheet', opt, {
                                label: `Sheet ${i.label || i.id || `#${idx + 1}`}`,
                                detailOverride: i.detail_override || {},
                                onSaveOverride: (override) => updateItem(idx, { detail_override: override }),
                              })}
                              title={hasOverride
                                ? 'Edit per-sheet override (or apply to master)'
                                : 'Edit master rate detail or set a per-sheet override'}
                              className={`inline-flex items-center gap-0.5 px-1.5 py-1 text-[10px] font-semibold rounded border ${
                                hasOverride
                                  ? 'text-amber-700 hover:text-amber-900 hover:bg-amber-50 border-amber-300 bg-amber-50/60'
                                  : 'text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 border-emerald-300'
                              }`}>
                        <Pencil size={9} /> {hasOverride ? 'Override' : 'Detail'}
                      </button>
                    )}
                    {i.rate_id && <EditRateLink component="sheet" rateId={i.rate_id} testid={`sheet-rate-edit-${idx}`} label="✎" />}
                  </div>
                </label>
                {/* Qty */}
                <label className="col-span-3 md:col-span-1 flex flex-col gap-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Qty</span>
                  <input type="number" step="1" min="0" value={i.qty ?? 0}
                         data-testid={`sheet-qty-${idx}`}
                         onChange={(e) => updateItem(idx, { qty: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                         className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
                </label>
                {/* Length */}
                <label className="col-span-4 md:col-span-2 flex flex-col gap-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Length (m)</span>
                  <input type="number" step="0.1" min="0" value={i.length_m ?? 0}
                         data-testid={`sheet-length-${idx}`}
                         onChange={(e) => updateItem(idx, { length_m: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                         className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
                </label>
                {/* Rate override */}
                <label className="col-span-4 md:col-span-2 flex flex-col gap-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">$/lm override</span>
                  <input type="number" step="0.01" min="0" value={i.rate_override ?? ''}
                         placeholder={opt ? opt.rate_per_lm.toFixed(2) : '—'}
                         data-testid={`sheet-rate-override-${idx}`}
                         onChange={(e) => updateItem(idx, { rate_override: e.target.value === '' ? null : parseFloat(e.target.value) })}
                         className="w-full px-2 py-1 text-xs border border-slate-300 rounded text-right font-mono-num" />
                </label>
                {/* Computed total LM + trash */}
                <div className="col-span-12 md:col-span-1 flex items-center justify-end gap-2">
                  <span className="text-[10px] text-slate-500 font-mono-num" title="Total linear metres">
                    {totalLm.toFixed(2)} m
                  </span>
                  <button type="button" data-testid={`sheet-remove-${idx}`}
                          onClick={() => removeRow(idx)}
                          className="text-rose-500 hover:text-rose-700"
                          title="Remove this sheet line">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        <button type="button" data-testid="sheet-add" onClick={addRow}
                className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:text-sky-900 hover:underline">
          <Plus size={11} /> Add sheet line
        </button>
      </div>
    </details>
  );
}


// ── Pipe/Flue Flashings (Btray + Dektite combo) ───────────────────────


function FlashingSection({ items = [], api, appraisalId }) {
  // Flashings + Dektites live in the shared Components list (one list, counted once).
  return (
    <details className="bg-white border border-slate-200 rounded-md" open data-testid="flashing-section">
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
        Flashing Components <span className="text-xs font-normal text-slate-500">({items.length} diagram pin{items.length === 1 ? '' : 's'} · priced in the list below)</span>
      </summary>
      <div className="px-4 pb-4 pt-2">
        {appraisalId && <ComponentsFlashingEmbed api={api} appraisalId={appraisalId} pinCount={items.length} />}
      </div>
    </details>
  );
}


// ── Calculated lines panel (mirror of the Concrete one) ───────────────


function CalculatedLinesPanel({ preview, showCalcs = false, toggleCalcs = () => {}, jumpToComponents = () => {} }) {
  const [filter, setFilter] = useState('all');
  const lines = preview.lines || [];
  const stageTotals = preview.stage_totals || { stage_1: 0, stage_2: 0 };
  const stageCounts = preview.stage_counts || {
    stage_1: lines.filter(l => (l.stage || 1) === 1).length,
    stage_2: lines.filter(l => (l.stage || 1) === 2).length,
  };
  const visible = lines.filter(l => {
    const s = Number(l.stage || 1);
    if (filter === 'stage1') return s === 1;
    if (filter === 'stage2') return s === 2;
    return true;
  });
  const visibleTotal = visible.reduce((s, l) => s + (Number(l.total) || 0), 0);
  return (
    <details className="bg-white border border-slate-200 rounded-md" open data-testid="iron-calculated-lines-panel">
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
        <Wrench size={13} /> Builder lines <span className="text-xs font-normal text-slate-500">(flashings &amp; Dektites are listed separately and added to the total)</span> <span className="text-xs font-normal text-slate-500">({lines.length})</span>
      </summary>
      <div className="px-4 pb-3 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap pt-1.5">
          <div className="inline-flex bg-slate-100 rounded p-0.5" role="tablist">
            {[
              { v: 'all', label: `All (${lines.length})`, cls: 'bg-slate-700' },
              { v: 'stage1', label: `Stage 1 — Repairs (${stageCounts.stage_1})`, cls: 'bg-sky-600' },
              { v: 'stage2', label: `Stage 2 — Paint (${stageCounts.stage_2})`, cls: 'bg-emerald-600' },
            ].map(opt => (
              <button key={opt.v} type="button" onClick={() => setFilter(opt.v)}
                      data-testid={`iron-calc-lines-filter-${opt.v}`}
                      className={`px-3 py-1 text-xs font-semibold rounded transition-all ${filter === opt.v ? `${opt.cls} text-white shadow-sm` : 'text-slate-600 hover:bg-white'}`}>{opt.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div><span className="text-slate-500">Stage 1</span> <span className="font-mono-num font-semibold text-sky-800">${stageTotals.stage_1.toFixed(2)}</span></div>
            <div><span className="text-slate-500">Stage 2</span> <span className="font-mono-num font-semibold text-emerald-800">${stageTotals.stage_2.toFixed(2)}</span></div>
            <div><span className="text-slate-500">Total</span> <span className="font-mono-num font-bold text-slate-900">${preview.total.toFixed(2)}</span></div>
            {/* v10.29 Sprint 4b · Slice H — CalcShadow toggle */}
            <button type="button"
                    onClick={toggleCalcs}
                    data-testid="iron-calcshadow-toggle"
                    className={`text-[10px] px-2 py-0.5 rounded border ${showCalcs ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                    title="Toggle calculation breakdown per line">
              {showCalcs ? '✓ calcs' : 'calcs'}
            </button>
          </div>
        </div>
        {visible.length === 0 ? (
          <div className="text-xs text-slate-500 py-2 italic">
            {lines.length === 0 ? 'No lines yet — enable Rescrew or pick a per-pin replacement.' : 'No lines match this filter.'}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left py-1.5 w-16">Stage</th>
                <th className="text-left">Item</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Rate</th>
                <th className="text-right">Total</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((l, i) => {
                const s = Number(l.stage || 1);
                const pill = s === 2
                  ? <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">Stage 2</span>
                  : <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-sky-100 text-sky-800 border border-sky-200">Stage 1</span>;
                return (
                  <tr key={i} className="border-b border-slate-100 last:border-0 align-top"
                      data-testid={`iron-line-${i}`}>
                    <td className="py-1.5">{pill}</td>
                    <td>
                      <div className="font-medium text-slate-800" data-testid={`iron-line-item-${i}`}>{l.type_name}</div>
                      {l.notes && <div className="text-[11px] text-slate-400 italic">{l.notes}</div>}
                      {/* v10.29 Sprint 4b · Slice H (2026-02c) — CalcShadow toggleable */}
                      {showCalcs && (
                        <div className="text-[10px] text-slate-500 italic font-mono-num mt-0.5"
                             data-testid={`iron-line-calc-${i}`}>
                          {l.quantity} {l.unit} × ${(l.rate || 0).toFixed(2)} ={' '}
                          <strong className="text-slate-700">${(l.total || 0).toFixed(2)}</strong>
                          {(l.materials_amount || l.labour_amount) > 0 && (
                            <span className="ml-2">
                              (M ${(l.materials_amount || 0).toFixed(2)} · L ${(l.labour_amount || 0).toFixed(2)})
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-right font-mono-num">{l.quantity} {l.unit}</td>
                    <td className="text-right font-mono-num text-slate-600">${l.rate.toFixed(2)}</td>
                    <td className="text-right font-mono-num font-semibold text-slate-800">${l.total.toFixed(2)}</td>
                    <td className="text-right">
                      {/* v10.29 Sprint 4b · Slice C — fast-jump */}
                      <button type="button"
                              onClick={() => jumpToComponents(l.component_type)}
                              className="w-5 h-5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                              title={`Open ${l.component_type} section in Components tab`}
                              data-testid={`iron-line-jump-${i}`}>
                        ↗
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filter !== 'all' && (
              <tfoot>
                <tr className="border-t-2 border-slate-300 font-bold">
                  <td colSpan={4} className="py-2 text-right text-slate-700">{filter === 'stage1' ? 'Stage 1 — Repairs sub-total' : 'Stage 2 — Paint sub-total'}</td>
                  <td className="py-2 text-right font-mono-num text-slate-900">${visibleTotal.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </details>
  );
}
